// Star ratings: the average (drawn stars, text for screen readers) and, where allowed, five
// radio buttons to rate a song 1–5. Drawn in CSS/inline SVG.
import { useEffect, useState } from "react";
import { LikeButton, loadRating, useRatings } from "./Like";
import { api, ApiError } from "../api";
import { currentRoute, Link } from "../lib/router";
import { useSession } from "../session";

export interface RatingView {
  average: number | null;
  count: number;
  mine: number | null;
  canRate: boolean;
  reason: string | null;
}

function Star({ fill, size }: { fill: number; size: number }) {
  // `fill` 0–1: the share of the star that is filled (for averages like 4.3).
  const id = `s${Math.round(fill * 100)}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id}>
          <stop offset={`${fill * 100}%`} stopColor="var(--star, #e69f00)" />
          <stop offset={`${fill * 100}%`} stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M12 2.5l2.9 6.1 6.6.8-4.9 4.6 1.3 6.6L12 17.3l-5.9 3.3 1.3-6.6-4.9-4.6 6.6-.8z"
        fill={`url(#${id})`}
        stroke="var(--star, #e69f00)"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Read-only stars for an average (null = no ratings yet). */
export function StarAverage({ average, count, size = 14 }: { average: number | null; count: number; size?: number }) {
  if (average === null || count === 0) return <span className="muted stars-none">No ratings yet</span>;
  return (
    <span
      className="stars"
      aria-label={`${average.toFixed(1)} out of 5 stars from ${count} rating${count === 1 ? "" : "s"}`}
    >
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={size} fill={Math.max(0, Math.min(1, average - i + 1))} />
      ))}
      <span className="stars-text" aria-hidden="true">
        {average.toFixed(1)} · {count}
      </span>
    </span>
  );
}

/** The rating of a song: average, and the viewer's own stars if they may rate it. */
export function RateSong({ loopId, author }: { loopId: string; author: string }) {
  const user = useSession((s) => s.user);
  const r = useRatings((s) => s.views[loopId] ?? null);
  const [hover, setHover] = useState<number | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    loadRating(loopId, true);
  }, [loopId, user?.id]);
  if (!r) return null;
  const rate = async (stars: number) => {
    setError("");
    try {
      const v = await api<RatingView>("PUT", `/api/loops/${encodeURIComponent(loopId)}/rating`, { stars });
      useRatings.getState().set(loopId, v);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Couldn't save your rating.");
    }
  };
  const shown = hover ?? r.mine ?? 0;
  return (
    <div className="rate-song" data-testid="rating">
      <StarAverage average={r.average} count={r.count} />
      {(r.canRate || r.mine !== null || !user) && <LikeButton loopId={loopId} author={author} withLabel />}
      {r.canRate ? (
        <div className="rate-stars" role="radiogroup" aria-label="Your rating" onMouseLeave={() => setHover(null)}>
          <span className="muted">{r.mine ? "Your rating" : "Rate it"}</span>
          {[1, 2, 3, 4, 5].map((i) => (
            <button
              key={i}
              type="button"
              role="radio"
              aria-checked={r.mine === i}
              aria-label={`${i} star${i === 1 ? "" : "s"}`}
              className="rate-star"
              onMouseEnter={() => setHover(i)}
              onFocus={() => setHover(i)}
              onBlur={() => setHover(null)}
              onClick={() => void rate(i)}
              data-testid={`rate-${i}`}
            >
              <Star size={22} fill={i <= shown ? 1 : 0} />
            </button>
          ))}
        </div>
      ) : (
        r.reason && (
          <span className="muted rate-reason">
            {r.reason === "Sign in to rate songs." ? (
              <>
                <Link href={`/signin?next=${encodeURIComponent(currentRoute())}`}>Sign in</Link> to rate songs.
              </>
            ) : (
              r.reason
            )}
          </span>
        )
      )}
      {error && (
        <span role="alert" className="error">
          {error}
        </span>
      )}
    </div>
  );
}
